import { toast } from 'react-toastify';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { Button, Divider, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@ds';

import { DrawerModal } from '@ui';
import { useCurrentUser } from '@lp/core/hooks/useCurrentUser';
import { CaseStatus } from '@/api/apis/case';
import { StatusGroup, useCasesBoardStore } from '@/store/cases-board.store';
import { caseStatusNames } from '@/constants/case/case.constants';
import { useItemsReordering } from '@/hooks/useItemsReordering';
import { StatusCard } from './StatusCard';
import { UserPreferences } from '@/api/apis/user';

type ModalPropsType = {
  onOpenChange: () => void;
};

export const RowsReorderModal = ({ onOpenChange }: ModalPropsType) => {
  const { updateUserPreferences } = useCurrentUser();
  const groupsOrdered = useCasesBoardStore((store) => store.groupsOrdered);
  const statusesOrdered = useCasesBoardStore((store) => store.statusesOrdered);
  const showGroups = useCasesBoardStore((store) => store.showGroups);
  const setBoardPreferences = useCasesBoardStore((store) => store.setBoardPreferences);
  const { items: rowsUpdated, moveItem } = useItemsReordering<StatusGroup | CaseStatus>(
    showGroups ? groupsOrdered : statusesOrdered,
  );

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = rowsUpdated.findIndex((item) =>
        typeof item === 'string' ? item === active.id : item.id === active.id,
      );
      const newIndex = rowsUpdated.findIndex((item) =>
        typeof item === 'string' ? item === over.id : item.id === over.id,
      );
      moveItem(oldIndex, newIndex);
    }
  };

  const changeRowsByStatus = async (statuses: CaseStatus[]) => {
    setBoardPreferences({ statusesOrdered: statuses });
    updateUserPreferences([{ name: UserPreferences.statusesOrdered, value: JSON.stringify(statuses) }]);
  };

  const changeRowsByGroups = (groups: StatusGroup[]) => {
    setBoardPreferences({ groupsOrdered: groups });
    updateUserPreferences([{ name: UserPreferences.groupsOrdered, value: JSON.stringify(groups) }]);
  };

  const saveRowsOrder = async (onClose: () => void) => {
    try {
      if (showGroups) {
        await changeRowsByGroups(rowsUpdated as StatusGroup[]);
      } else {
        await changeRowsByStatus(rowsUpdated as CaseStatus[]);
      }
      toast.success('Rows have been saved successfully');
    } catch {
      toast.error('An error occurred while saving rows');
    } finally {
      onClose();
    }
  };

  const getItemIds = () => rowsUpdated.map((item) => (typeof item === 'string' ? item : item.id));

  const renderGroupCards = (data: StatusGroup[]) => {
    return data.map((group) => <StatusCard key={group.id} id={group.id} title={group.name} group={group} />);
  };

  const renderStatusCards = (data: CaseStatus[]) => {
    return data.map((status) => (
      <StatusCard key={status} id={status} title={caseStatusNames[status]} status={status} />
    ));
  };

  return (
    <DrawerModal isOpen onOpenChange={onOpenChange} className="min-w-lg w-max max-w-[90vw]">
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader>
              <div className="flex flex-col w-full gap-4">
                <h3 className="text-hl text-black">Rearrange Lanes</h3>
                <Divider />
              </div>
            </ModalHeader>
            <ModalBody>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToVerticalAxis]}
              >
                <SortableContext items={getItemIds()} strategy={verticalListSortingStrategy}>
                  <div className="flex flex-col">
                    {showGroups
                      ? renderGroupCards(rowsUpdated as StatusGroup[])
                      : renderStatusCards(rowsUpdated as CaseStatus[])}
                  </div>
                </SortableContext>
              </DndContext>
            </ModalBody>
            <ModalFooter>
              <Divider />
              <div className="flex gap-3">
                <Button variant="outline" onPress={onClose} className="flex-1">
                  Discard
                </Button>
                <Button variant="solid" onPress={() => saveRowsOrder(onClose)} className="flex-1">
                  Save
                </Button>
              </div>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </DrawerModal>
  );
};
